import { Box, Switch, Tooltip, styled } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PaloTotalsBar, PaloTotalsEntry, PaloTotalsFact } from '@synergy/components-palo';
import { PoolNumberTextfield } from '@synergy/components-next';
import { ReactElement } from 'react';
import { getCurrencySymbol, getLocalizedString } from '@synergy/utils';

import { FinancialCalculations } from '../../../../common/utils/financial-calculations';
import { InvoiceSteps } from '../models/invoice-steps';
import { bestLocaleSelector } from '../../../../common/selectors/locale-selectors';
import { defaultCurrencySelector } from '../../../../common/selectors/currency-selectors';
import { invoiceDetailOverviewDataSelector } from '../store/overview/invoice-detail-overview-selectors';
import {
    invoiceDetailPositionsBaselineSelector,
    invoiceDetailPositionsDataSelector,
} from '../store/positions/invoice-detail-positions-selectors';
import { invoiceDetailStepperStateSelector } from '../store/invoice-detail-selectors';
import { isInvoiceTextItem } from '../../models/invoice-item';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useInvoiceDetailContext } from '../../context/invoice-detail-context';
import { usePaloDirtyMark } from '../../../../common/components/palo/palo-dirty-context';

/** The name the totals report their unsaved input under. */
export const INVOICE_TOTALS_DIRTY = 'invoice-totals';

const amount = (value: number | string | undefined | null): number => Number(value || 0);

/** Narrow enough for the row, wide enough for a five-digit amount. */
const AmountField = styled(PoolNumberTextfield)(({ theme }) => ({
    // Narrow enough for the row, wide enough for a five-digit amount; the
    // height is the one every other field in the mask has.
    width: theme.spacing(13),

    '& > div': {
        fontSize: '12.5px',

        '& > input': {
            textAlign: 'right',
            fontSize: '12.5px',
        },
    },
}));

/**
 * What the invoice comes to, in one row above the actions.
 *
 * It replaces the "Gesamtübersicht" and the second set of totals under the
 * position table. Shipping, surcharge and tax-free are fields here rather than
 * buttons that open one: they are filled while the positions are worked on, and
 * a figure already entered has to be readable at a glance (Roland Niedermayr,
 * 2026-09-23).
 */
export const PaloInvoiceTotalsBar = (): ReactElement => {
    const defaultCurrency = useAppSelector(defaultCurrencySelector);
    const { values } = useAppSelector(invoiceDetailOverviewDataSelector);
    const currency = values.currency ?? defaultCurrency;
    const locale = useAppSelector(bestLocaleSelector);
    const locales = useAppSelector(state => state.supportedLocales);
    const readonly = useAppSelector(invoiceDetailStepperStateSelector).readonlySteps.includes(InvoiceSteps.Positions);
    const {
        invoiceItems,
        netShippingCosts,
        setNetShippingCosts,
        netSurcharge,
        setNetSurcharge,
        isTaxFree,
        setIsTaxFree,
    } = useInvoiceDetailContext();

    // The positions reach the context from the step that edits them, so before
    // that step has been open the bar would add up an invoice it has not seen.
    const stored = useAppSelector(invoiceDetailPositionsDataSelector).values;
    const known = invoiceItems.length > 0;
    const items = known
        ? invoiceItems
        : (stored.allItems.filter(item => !isInvoiceTextItem(item)) as typeof invoiceItems);
    const shippingCosts = known ? netShippingCosts : stored.netShippingCosts;
    const surcharge = known ? netSurcharge : stored.netSurcharge;
    const taxFree = known ? isTaxFree : stored.isTaxFree;

    // These figures are no form field, so no form saw them change: a new
    // shipping cost offered no save and was lost on leaving. Compared against
    // the invoice as the server has it.
    const baseline = useAppSelector(invoiceDetailPositionsBaselineSelector);
    usePaloDirtyMark(
        INVOICE_TOTALS_DIRTY,
        !readonly &&
            (amount(netShippingCosts) !== amount(baseline?.netShippingCosts) ||
                amount(netSurcharge) !== amount(baseline?.netSurcharge) ||
                !!isTaxFree !== !!baseline?.isTaxFree)
    );

    const calculations = FinancialCalculations.calculate(items, shippingCosts, surcharge);
    const taxKeys = Object.keys(calculations.taxes).sort().reverse();
    const taxTotal = taxKeys.reduce(
        (sum, key) => sum + calculations.taxes[key as unknown as keyof typeof calculations.taxes].tax,
        0
    );

    const money = (value: number): ReactElement => (
        <FormattedNumber
            value={value}
            currency={currency}
            style="currency"
            minimumFractionDigits={2}
            maximumFractionDigits={2}
        />
    );

    const paymentTerm = values.paymentTerm
        ? getLocalizedString(locales, values.paymentTerm.text)
        : values.supplierAccount?.termsOfPayment;
    const deliveryTerm = values.supplierAccount?.termsOfDelivery;

    const entries: PaloTotalsEntry[] = [
        { label: <FormattedMessage id="app.label.netPrice" />, value: money(calculations.netTotal) },
        ...(taxFree
            ? []
            : [
                  {
                      // One line whatever the invoice mixes; the rates are in the
                      // tooltip, where two of them do not cost a column.
                      label: (
                          <Tooltip
                              title={taxKeys.map(key => (
                                  <Box key={key}>
                                      {key} %:{' '}
                                      {money(calculations.taxes[key as unknown as keyof typeof calculations.taxes].tax)}
                                  </Box>
                              ))}
                          >
                              <Box component="span">
                                  <FormattedMessage id="app.label.tax" />
                                  {1 === taxKeys.length && ` ${taxKeys[0]} %`}
                              </Box>
                          </Tooltip>
                      ),
                      value: money(taxTotal),
                  },
              ]),
        {
            label: <FormattedMessage id="app.label.invoiceTotal" />,
            value: money(taxFree ? calculations.netTotal : calculations.grossTotal),
            total: true,
        },
    ];

    return (
        <PaloTotalsBar
            entries={entries}
            facts={
                <>
                    {paymentTerm && (
                        <PaloTotalsFact
                            label={<FormattedMessage id="app.label.paymentTerm" />}
                            title={paymentTerm}
                            flexible={true}
                        >
                            {paymentTerm}
                        </PaloTotalsFact>
                    )}
                    {deliveryTerm && (
                        <PaloTotalsFact
                            label={<FormattedMessage id="app.label.termsOfDelivery" />}
                            title={deliveryTerm}
                            flexible={true}
                        >
                            {deliveryTerm}
                        </PaloTotalsFact>
                    )}
                    <PaloTotalsFact label={<FormattedMessage id="app.label.deliverCosts" />}>
                        <AmountField
                            value={shippingCosts}
                            onChange={event => setNetShippingCosts?.(Number(event.target.value))}
                            disabled={readonly}
                            fixedDecimalScale={true}
                            decimalScale={2}
                            endAdornment={getCurrencySymbol(currency, locale)}
                        />
                    </PaloTotalsFact>
                    <PaloTotalsFact label={<FormattedMessage id="app.label.surcharges" />}>
                        <AmountField
                            value={surcharge}
                            onChange={event => setNetSurcharge?.(Number(event.target.value))}
                            disabled={readonly}
                            fixedDecimalScale={true}
                            decimalScale={2}
                            endAdornment={getCurrencySymbol(currency, locale)}
                        />
                    </PaloTotalsFact>
                    <PaloTotalsFact label={<FormattedMessage id="app.label.taxFree" />}>
                        <Switch
                            checked={taxFree}
                            onChange={event => setIsTaxFree?.(event.target.checked)}
                            color="primary"
                            size="small"
                            disabled={readonly}
                            inputProps={{ 'aria-label': 'Steuerfrei' }}
                        />
                    </PaloTotalsFact>
                </>
            }
        />
    );
};
